<template>
    <div class="stl-hh-timeline">
        <div v-if="loading" class="stl-loading">
            <b-spinner small variant="secondary" />
            <span>세대 타임라인 조회 중...</span>
        </div>

        <template v-else-if="data">
            <div class="stl-row" style="margin-bottom: 8px">
                <strong>{{ data.roomName || "-" }}</strong>
                <span v-if="data.exclusiveArea" class="stl-hint">전용 {{ fmtArea(data.exclusiveArea) }}㎡</span>
                <span class="stl-hint">확정 {{ data.summary ? data.summary.months : 0 }}개월</span>
            </div>

            <div class="stl-table-container stl-table-fill">
                <table class="stl-table">
                    <thead>
                        <tr>
                            <th>청구월</th>
                            <th v-if="showEnergyColumn">에너지</th>
                            <th>청구기간</th>
                            <th class="num">
                                사용량<span v-if="!showEnergyColumn">({{ unitLabel(usageUnit) }})</span>
                            </th>
                            <th class="num">{{ L.usageShare }}</th>
                            <th v-if="!showEnergyColumn && showAreaColumn" class="num">{{ L.areaShare }}</th>
                            <th v-else-if="showEnergyColumn" class="num">{{ L.areaShare }}</th>
                            <th class="num">조정</th>
                            <th class="num">합계</th>
                            <th class="num">전월 대비</th>
                            <th>상태</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="(row, idx) in data.rows"
                            :key="row.settlementIdx"
                            class="clickable-row"
                            @click="$emit('select', row)"
                        >
                            <td>{{ fmtYm(row.regYm) }}</td>
                            <td v-if="showEnergyColumn">{{ energyRowLabel(row.energySource) }}</td>
                            <td class="stl-hint">
                                {{ fmtDate(row.billingStartAt) }} ~ {{ fmtDate(row.billingEndAt) }}
                            </td>
                            <td class="num">
                                {{ fmtUsage(row.usageQty) }}
                                <span v-if="showEnergyColumn" class="microcap dim">{{ unitLabel(rowUsageUnit(row)) }}</span>
                            </td>
                            <td class="num">{{ fmtWon(row.individualCost) }}</td>
                            <td v-if="rowShowArea(row)" class="num">{{ fmtWon(row.allocatedCommonCost) }}</td>
                            <td v-else-if="showEnergyColumn" class="num dim">—</td>
                            <td class="num">{{ fmtWon(row.manualAdjustAmount) }}</td>
                            <td class="num">
                                <strong>{{ fmtWon(row.totalAmount) }}</strong>
                            </td>
                            <td class="num" :class="deltaClass(idx)">{{ fmtDeltaPct(monthOverMonthPct(idx)) }}</td>
                            <td>
                                <span :class="statusClass(row.status)">{{ statusLabel(row.status) }}</span>
                            </td>
                        </tr>
                        <tr v-if="!data.rows || !data.rows.length">
                            <td :colspan="tableColspan">
                                <div class="stl-empty"><p>기간 내 확정된 정산 내역이 없습니다.</p></div>
                            </td>
                        </tr>
                    </tbody>
                    <tfoot v-if="data.rows && data.rows.length">
                        <tr>
                            <td :colspan="showEnergyColumn ? 3 : 2">기간 합계 ({{ data.summary.months }}개월)</td>
                            <td class="num">{{ showEnergyColumn ? "—" : fmtUsage(data.summary.usageQtySum) }}</td>
                            <td :colspan="showEnergyColumn ? 4 : showAreaColumn ? 4 : 3"></td>
                            <td class="num">{{ fmtWon(data.summary.totalAmountSum) }}</td>
                            <td></td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </template>

        <div v-else class="stl-empty">
            <p>세대를 선택하면 월별 정산 타임라인이 표시됩니다.</p>
        </div>
    </div>
</template>

<script>
import {
    fmtWon,
    fmtKwh,
    fmtUsage,
    fmtArea,
    fmtYm,
    fmtDate,
    fmtDeltaPct,
    statusLabel,
    statusClass,
    unitLabel,
    SETTLEMENT_LABELS,
} from "../utils/settlementFormat.js";

export default {
    name: "HouseholdHistoryTimeline",
    props: {
        data: { type: Object, default: null },
        loading: { type: Boolean, default: false },
        usageUnit: { type: String, default: "kWh" },
        showAreaColumn: { type: Boolean, default: true },
        showEnergyColumn: { type: Boolean, default: false },
    },
    computed: {
        L() {
            return SETTLEMENT_LABELS;
        },
        tableColspan() {
            if (this.showEnergyColumn) return 10;
            return this.showAreaColumn ? 9 : 8;
        },
    },
    methods: {
        fmtWon,
        fmtKwh,
        fmtUsage,
        fmtArea,
        fmtYm,
        fmtDate,
        fmtDeltaPct,
        statusLabel,
        statusClass,
        unitLabel,
        energyRowLabel(e) {
            if (e === "Gas") return "가스";
            if (e === "Water") return "수도";
            return "전기";
        },
        rowUsageUnit(row) {
            if (row.usageUnit) return row.usageUnit;
            if (row.energySource === "Gas") return "Nm3";
            if (row.energySource === "Water") return "m3";
            return "kWh";
        },
        rowShowArea(row) {
            if (this.showEnergyColumn) return String(row.energySource) === "Elec";
            return this.showAreaColumn;
        },
        /** rows는 regYm DESC — 동일 에너지 기준 전월 대비 */
        monthOverMonthPct(idx) {
            const rows = this.data?.rows;
            if (!rows || idx >= rows.length - 1) return null;
            const cur = rows[idx];
            let prevIdx = idx + 1;
            if (this.showEnergyColumn) {
                while (prevIdx < rows.length && rows[prevIdx].energySource !== cur.energySource) {
                    prevIdx += 1;
                }
            }
            if (prevIdx >= rows.length) return null;
            const curAmt = parseFloat(cur.totalAmount || 0);
            const prevAmt = parseFloat(rows[prevIdx].totalAmount || 0);
            if (!prevAmt) return null;
            return ((curAmt - prevAmt) / prevAmt) * 100;
        },
        deltaClass(idx) {
            const p = this.monthOverMonthPct(idx);
            if (p === null || isNaN(p)) return "dim";
            if (p > 0) return "stl-delta-up";
            if (p < 0) return "stl-delta-down";
            return "dim";
        },
    },
};
</script>

<style scoped>
.stl-delta-up {
    color: #c0392b;
}
.stl-delta-down {
    color: #2980b9;
}
</style>
